import { isEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import { useMount } from 'hooks';
import { AICreateThreadOutput } from 'types';
import { useAIContext } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/AIContextProvider';
import { removeCitations } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/citations-utils';
import { CreateThreadAction } from 'broker/pages/SubmissionWorkspacePage/contexts/ai/SingleAIFeatureContextProvider';

export const useCreateAIThread = (
  createThread: CreateThreadAction,
  creationCondition = true,
  callback?: (response: AICreateThreadOutput | null) => void,
) => {
  const { createAIThread, messages, setCreateThreadAction } = useAIContext();

  const wasCreatedRef = useRef(false);

  useMount(() => {
    setCreateThreadAction(() => createThread);
  });

  // This effect should only run once when the component mounts OR the first time the creationCondition changes to true
  useEffect(() => {
    (async () => {
      if (wasCreatedRef.current) {
        return; // already executed
      }
      if (!creationCondition || !isEmpty(messages)) {
        return; // conditions not met
      }

      wasCreatedRef.current = true;

      const response = await createAIThread(createThread);

      // Stripping the response from citations, these are only used in the AI popup, not in the features themselves
      const strippedResponse = response
        ? { ...response, response: removeCitations(response?.response, response?.citations) }
        : response;

      callback?.(strippedResponse);
    })();
  }, [creationCondition, messages, createAIThread, createThread, callback, setCreateThreadAction]);
};
